/**
 * Quiz Store (V2)
 *
 * Central state management for quiz playing experience.
 * Uses sync external store pattern (proven from V1).
 *
 * V2 Enhancements:
 * - Category tracking (phone/admin/cities)
 * - Granularity level tracking
 * - Region tracking for knowledge state
 * - Attempt-level tracking (not just session)
 * - Knowledge state updates
 */

import { useSyncExternalStoreWithSelector } from "use-sync-external-store/with-selector";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";
import type { Category, MapDisplaySettings } from "@/db";
import { db } from "@/db/database";
import type { QuizAttempt, QuizSession, KnowledgeState } from "@/db/types";
import { calculateDifficultyMultiplier } from "@/utils/difficultyCalculator";
import { syncQuizRunToSupabase } from "@/lib/quizRunSync";
import { getFeatureIdentifier } from "@/utils/geojsonLoader";

// ============================================================================
// Types
// ============================================================================

export type QuizMode = "choice" | "setup" | "playing" | "complete";

export type AnswerStatus = "correct" | "incorrect";

export type QuizFeature = {
  id: string;
  feature: Feature<Geometry, GeoJsonProperties>;
  label: string;
};

export type PromptPart = {
  key: string;
  value: string;
};

/**
 * Prompt Group
 * Groups multiple features that share the same prompt values
 * Example: Multiple provinces in the same region = one question
 */
export type PromptGroup = {
  id: string; // Unique identifier for this prompt combination
  value: string; // Display value for the prompt
  featureIds: string[]; // Features that match this prompt
  parts: PromptPart[]; // Prompt parts (key-value pairs)
};

export type QuizAttemptRecord = {
  featureId: string; // The CORRECT answer feature ID
  clickedFeatureId: string; // The feature the user actually CLICKED
  promptId: string;
  prompt: string;
  promptParts: PromptPart[];
  status: AnswerStatus;
  label: string;
  attemptsUsed: number;
  timeSpentMs: number;
};

// Runtime quiz configuration (simplified for store)
export type QuizRuntimeConfig = {
  category: Category;
  geography: string; // Country/region name (e.g., "Indonesia", "United States")
  region: string; // Quiz name (e.g., "Indonesia All Kabupaten")
  granularityLevel: number; // 0=country, 1=admin1, etc.
  quizSignature: string; // Quiz ID (e.g., "qz_abc123")
  datasetSignature: string; // Dataset signature for knowledge state grouping
  promptKeys: string[]; // Properties to use for prompts
  maxAttemptsPerPrompt: number; // 1-5
  repetitions: number; // How many times each prompt appears
};

type QuizState = {
  // Configuration
  config: QuizRuntimeConfig | null;
  collection: FeatureCollection<Geometry, GeoJsonProperties> | null;

  // Features
  features: QuizFeature[];

  // Prompt Groups (unique prompt combinations)
  promptGroups: PromptGroup[];
  promptGroupMap: Record<string, PromptGroup>; // Quick lookup by ID

  // Quiz Progress
  mode: QuizMode;
  order: string[]; // Prompt Group IDs in quiz order
  currentIndex: number;
  currentPromptAttempts: number;

  // Answers
  selections: Record<string, AnswerStatus>;
  attempts: QuizAttemptRecord[];
  lastAnswer: QuizAttemptRecord | null;

  // Session tracking
  sessionId: string | null;
  sessionStartTime: number | null;
  questionStartTime: number | null;

  // Map Display Settings
  mapSettings: MapDisplaySettings;
  settingsLockedAt: Date | null; // Lock settings after first question
};

type Listener = () => void;
type Selector<T> = (state: QuizState) => T;

// ============================================================================
// Store Implementation
// ============================================================================

const listeners = new Set<Listener>();

const initialState: QuizState = {
  config: null,
  collection: null,
  features: [],
  promptGroups: [],
  promptGroupMap: {},
  mode: "choice",
  order: [],
  currentIndex: 0,
  currentPromptAttempts: 0,
  selections: {},
  attempts: [],
  lastAnswer: null,
  sessionId: null,
  sessionStartTime: null,
  questionStartTime: null,
  mapSettings: {
    hideOutlines: false,        // Show borders by default
    disableHover: false,         // Enable hover by default
    hideIndicatorColors: false,  // Show feedback by default
    hideLabels: false,           // Show labels by default
  },
  settingsLockedAt: null,
};

// ============================================================================
// LocalStorage Persistence for Map Settings
// ============================================================================

const MAP_SETTINGS_STORAGE_KEY = "mapquizzr_map_settings";

function saveMapSettingsToLocalStorage(settings: MapDisplaySettings) {
  try {
    localStorage.setItem(MAP_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn("Failed to save map settings to localStorage:", error);
  }
}

function loadMapSettingsFromLocalStorage(): MapDisplaySettings | null {
  try {
    const stored = localStorage.getItem(MAP_SETTINGS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as MapDisplaySettings;
    }
  } catch (error) {
    console.warn("Failed to load map settings from localStorage:", error);
  }
  return null;
}

// ============================================================================
// State Management
// ============================================================================

let quizState: QuizState = initialState;

function emit() {
  listeners.forEach((listener) => listener());
}

function setState(updater: (state: QuizState) => QuizState) {
  quizState = updater(quizState);
  emit();
}

function resetState(partial?: Partial<QuizState>) {
  // Load persisted map settings (if available)
  const persistedSettings = loadMapSettingsFromLocalStorage();

  quizState = {
    ...initialState,
    // Apply persisted settings if available
    ...(persistedSettings ? { mapSettings: persistedSettings } : {}),
    ...partial,
  };
  emit();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return quizState;
}

// ============================================================================
// Hook
// ============================================================================

export function useQuizStore<T>(
  selector: Selector<T>,
  isEqual?: (a: T, b: T) => boolean
): T {
  return useSyncExternalStoreWithSelector(
    subscribe,
    getSnapshot,
    getSnapshot,
    selector,
    isEqual
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract string values from a property value
 * Handles strings, arrays of strings, numbers
 */
function extractStringValues(value: unknown): string[] {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter((v) => v != null).map(String);
  }
  return [String(value)];
}

/**
 * Generate cartesian product of arrays
 * Example: [[1,2], [3,4]] → [[1,3], [1,4], [2,3], [2,4]]
 */
function cartesianProduct<T>(arrays: T[][]): T[][] {
  if (arrays.length === 0) return [];
  if (arrays.length === 1) return arrays[0]!.map((item) => [item]);

  const [first, ...rest] = arrays;
  const restProduct = cartesianProduct(rest);

  return first!.flatMap((item) => restProduct.map((prod) => [item, ...prod]));
}

/**
 * Encode a string for use in IDs (URL-safe)
 */
function encodePart(str: string): string {
  return encodeURIComponent(str);
}

/**
 * Build prompt groups from features
 * Groups features by their prompt property values
 * Handles arrays (creates multiple prompts per feature)
 */
function buildPromptGroups(
  features: QuizFeature[],
  promptKeys: string[]
): {
  promptGroups: PromptGroup[];
  promptGroupMap: Record<string, PromptGroup>;
} {
  const groups = new Map<string, PromptGroup>();

  for (const item of features) {
    // Extract values for each prompt key
    const valueSets = promptKeys.map((key) =>
      extractStringValues(item.feature.properties?.[key])
    );

    // Skip features with missing values
    if (valueSets.some((set) => set.length === 0)) {
      continue;
    }

    // Generate all combinations (for array properties)
    const combinations = cartesianProduct(valueSets);
    if (combinations.length === 0) {
      continue;
    }

    for (const combination of combinations) {
      const parts: PromptPart[] = combination.map((value, index) => ({
        key: promptKeys[index] ?? "",
        value,
      }));

      // Create unique ID for this prompt combination
      const groupId = parts
        .map((part) => `${encodePart(part.key)}=${encodePart(part.value)}`)
        .join("||");

      const entry = groups.get(groupId);
      if (entry) {
        // Add this feature to existing group
        if (!entry.featureIds.includes(item.id)) {
          entry.featureIds.push(item.id);
        }
      } else {
        // Create new group
        groups.set(groupId, {
          id: groupId,
          value: parts.map((part) => `${part.key}: ${part.value}`).join("\n"),
          featureIds: [item.id],
          parts,
        });
      }
    }
  }

  // Convert to array and sort
  const promptGroups: PromptGroup[] = Array.from(groups.values()).sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { numeric: true })
  );

  // Build lookup map
  const promptGroupMap: Record<string, PromptGroup> = {};
  for (const group of promptGroups) {
    promptGroupMap[group.id] = group;
  }

  return { promptGroups, promptGroupMap };
}

// ============================================================================
// Actions
// ============================================================================

/**
 * Initialize a new quiz
 */
export function initializeQuiz(
  config: QuizRuntimeConfig,
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  skipChoice = false
) {
  // Generate features with stable IDs using the same logic as the map component
  const features: QuizFeature[] = collection.features.map((feature, index) => {
    // Use getFeatureIdentifier to ensure consistent IDs across the app
    const id = getFeatureIdentifier(feature, index);
    const properties = feature.properties ?? {};

    // Get label from first prompt key
    const labelKey = config.promptKeys[0];
    const label = labelKey && properties[labelKey] ? String(properties[labelKey]) : id;

    return { id, feature, label };
  });

  // Build prompt groups (handles arrays and grouping by shared values)
  const { promptGroups, promptGroupMap } = buildPromptGroups(features, config.promptKeys);

  console.log("Prompt groups built:", {
    features: features.length,
    uniquePrompts: promptGroups.length,
    sampleGroups: promptGroups.slice(0, 3).map(g => ({
      id: g.id,
      parts: g.parts,
      featureCount: g.featureIds.length
    }))
  });

  // Create prompt order (using :: as delimiter for repetitions)
  const order: string[] = [];
  for (let rep = 0; rep < config.repetitions; rep++) {
    promptGroups.forEach((group) => {
      order.push(`${group.id}::${rep}`);
    });
  }

  // Shuffle order
  const shuffledOrder = [...order].sort(() => Math.random() - 0.5);

  console.log("Quiz order created:", {
    totalQuestions: shuffledOrder.length,
    uniquePrompts: promptGroups.length,
    repetitions: config.repetitions,
    samplePrompts: shuffledOrder.slice(0, 3),
  });

  resetState({
    config,
    collection,
    features,
    promptGroups,
    promptGroupMap,
    mode: skipChoice ? "playing" : "choice",
    order: shuffledOrder,
    currentIndex: 0,
    currentPromptAttempts: 0,
    selections: {},
    attempts: [],
    sessionId: `session-${Date.now()}`,
    sessionStartTime: skipChoice ? Date.now() : null,
    questionStartTime: skipChoice ? Date.now() : null,
  });
}

/**
 * Choose to explore the map (from choice screen or completion screen)
 */
export function chooseExplore(): void {
  setState((state) => {
    if (state.mode !== "choice" && state.mode !== "complete") {
      console.warn("chooseExplore() can only be called from choice or complete mode");
      return state;
    }
    return {
      ...state,
      mode: "setup",
    };
  });
}

/**
 * Choose to start quiz directly (from choice screen)
 */
export function chooseQuiz(): void {
  setState((state) => {
    if (state.mode !== "choice") {
      console.warn("chooseQuiz() can only be called from choice mode");
      return state;
    }
    return {
      ...state,
      mode: "playing",
      sessionStartTime: Date.now(),
      questionStartTime: Date.now(),
    };
  });
}

/**
 * Start the quiz
 */
export function startQuiz() {
  console.log("Starting quiz...");
  setState((state) => {
    console.log("Current state:", {
      mode: state.mode,
      features: state.features.length,
      order: state.order.length,
      currentIndex: state.currentIndex,
    });
    return {
      ...state,
      mode: "playing",
      sessionStartTime: Date.now(),
      questionStartTime: Date.now(),
    };
  });
}

/**
 * Submit an answer for the current prompt
 */
export function submitAnswer(clickedFeatureId: string) {
  const state = quizState;

  if (state.mode !== "playing" || !state.config) {
    console.warn("Cannot submit answer - quiz not in playing mode");
    return;
  }

  const currentPromptId = state.order[state.currentIndex];
  if (!currentPromptId) {
    console.warn("No current prompt");
    return;
  }

  // Extract group ID from prompt ID (format: "groupId::repIndex")
  const groupId = currentPromptId.split("::")[0];
  if (!groupId) {
    console.warn("Invalid prompt ID format:", currentPromptId);
    return;
  }

  // Get the prompt group
  const promptGroup = state.promptGroupMap[groupId];
  if (!promptGroup) {
    console.warn("Prompt group not found:", groupId);
    return;
  }

  // Check if clicked feature is in the group's valid answers
  const isCorrect = promptGroup.featureIds.includes(clickedFeatureId);
  const status: AnswerStatus = isCorrect ? "correct" : "incorrect";

  console.log("Checking answer:", {
    clickedFeatureId,
    validFeatureIds: promptGroup.featureIds,
    isCorrect
  });

  // Use first valid feature as the "correct" answer for record keeping
  const correctFeatureId = promptGroup.featureIds[0] ?? groupId;
  const feature = state.features.find((f) => f.id === correctFeatureId);
  if (!feature) {
    console.warn("Feature not found:", correctFeatureId);
    return;
  }

  // Calculate time spent
  const timeSpentMs = state.questionStartTime
    ? Date.now() - state.questionStartTime
    : 0;

  // Use prompt parts from the group
  const promptParts = promptGroup.parts;
  const prompt = promptParts.map((p) => p.value).join(", ");

  const attemptRecord: QuizAttemptRecord = {
    featureId: correctFeatureId, // First valid feature (for record keeping)
    clickedFeatureId, // The feature the user actually clicked
    promptId: currentPromptId,
    prompt,
    promptParts,
    status,
    label: feature.label,
    attemptsUsed: state.currentPromptAttempts + 1,
    timeSpentMs,
  };

  setState((s) => {
    const newAttempts = s.currentPromptAttempts + 1;
    const maxReached = newAttempts >= (s.config?.maxAttemptsPerPrompt ?? 1);
    const shouldAdvance = isCorrect || maxReached;

    return {
      ...s,
      currentPromptAttempts: shouldAdvance ? 0 : newAttempts,
      currentIndex: shouldAdvance ? s.currentIndex + 1 : s.currentIndex,
      selections: {
        ...s.selections,
        [currentPromptId]: status,
      },
      attempts: [...s.attempts, attemptRecord],
      lastAnswer: attemptRecord,
      questionStartTime: shouldAdvance ? Date.now() : s.questionStartTime,
      mode: shouldAdvance && s.currentIndex + 1 >= s.order.length ? "complete" : s.mode,
    };
  });

  // Lock map settings after first answer
  lockMapSettings();
}

/**
 * Reset the quiz
 */
export function resetQuiz() {
  resetState();
}

/**
 * Update map display settings
 * Settings can only be changed before the first question is answered
 * Settings are persisted to localStorage for reuse across quizzes
 */
export function updateMapSettings(settings: Partial<MapDisplaySettings>) {
  if (quizState.settingsLockedAt) {
    console.warn("Map settings are locked after the first question");
    return;
  }

  const newSettings = {
    ...quizState.mapSettings,
    ...settings,
  };

  setState((s) => ({
    ...s,
    mapSettings: newSettings,
  }));

  // Persist settings to localStorage for reuse across quizzes
  saveMapSettingsToLocalStorage(newSettings);
}

/**
 * Lock map settings after the first question
 * Called automatically by submitAnswer
 */
function lockMapSettings() {
  if (!quizState.settingsLockedAt && quizState.currentIndex > 0) {
    setState((s) => ({
      ...s,
      settingsLockedAt: new Date(),
    }));
  }
}

/**
 * Save quiz results to IndexedDB
 * Call this when quiz is complete
 */
export async function saveQuizResults(): Promise<void> {
  const state = quizState;

  if (!state.config || !state.sessionId) {
    console.warn("Cannot save quiz results: missing config or session ID");
    return;
  }

  // Store non-null values in local variables for TypeScript
  const config = state.config;
  const sessionId = state.sessionId;
  const { category, geography, region, granularityLevel, quizSignature } = config;
  const completedAt = new Date().toISOString();

  console.log("Saving quiz results to IndexedDB...", { quizSignature, attempts: state.attempts.length });

  try {
    // 1. Save all attempts to quiz_attempts table
    const attemptRecords: QuizAttempt[] = state.attempts.map((attempt, index) => {
      // Get the clicked feature label
      const clickedFeature = state.features.find((f) => f.id === attempt.clickedFeatureId);

      return {
        id: `${sessionId}-${attempt.promptId}-${index}-${Date.now()}`, // Make ID truly unique with index and timestamp
        quizSignature,
        sessionId,
        promptText: attempt.prompt,
        promptKeys: config.promptKeys,
        featureId: attempt.featureId, // Correct answer
        featureLabel: attempt.label, // Correct answer label
        clickedFeatureId: attempt.clickedFeatureId, // What user clicked
        clickedFeatureLabel: clickedFeature?.label, // Label of what they clicked
        category,
        region,
        granularityLevel,
        wasCorrect: attempt.status === "correct",
        attemptsUsed: attempt.attemptsUsed,
        timeSpentMs: attempt.timeSpentMs,
        attemptedAt: completedAt,
      };
    });

    await db.quiz_attempts.bulkPut(attemptRecords); // Use bulkPut instead of bulkAdd to allow overwrites
    console.log(`✓ Saved ${attemptRecords.length} attempts to database`);

    // 2. Save quiz session
    const stats = getQuizStats();

    // Filter out anomalous question times (>5 minutes indicates AFK)
    const MAX_QUESTION_TIME_MS = 5 * 60 * 1000; // 5 minutes
    const validQuestionTimes = attemptRecords
      .map(a => a.timeSpentMs)
      .filter(time => time <= MAX_QUESTION_TIME_MS);

    // Calculate time metrics from valid questions only
    const totalTimeMs = validQuestionTimes.reduce((sum, time) => sum + time, 0);
    const averageTimePerQuestionMs = validQuestionTimes.length > 0
      ? totalTimeMs / validQuestionTimes.length
      : 0;

    // Log if we filtered out any anomalies
    if (validQuestionTimes.length < attemptRecords.length) {
      console.log(`⚠️ Filtered out ${attemptRecords.length - validQuestionTimes.length} anomalous question times (>5min)`);
    }

    // Calculate difficulty multiplier based on display settings
    const difficultyMultiplier = calculateDifficultyMultiplier(state.mapSettings);

    const sessionRecord: QuizSession = {
      id: sessionId,
      quizSignature,
      totalQuestions: stats.totalQuestions,
      correctAnswers: stats.correctAnswers,
      incorrectAnswers: stats.incorrectAnswers,
      accuracyRate: stats.accuracyRate,
      totalTimeMs,
      averageTimePerQuestionMs,
      displaySettings: state.mapSettings, // Backlog #04: Track display settings
      difficultyMultiplier, // Backlog #04: Track difficulty multiplier
      completedAt,
    };

    await db.quiz_sessions.add(sessionRecord);
    console.log("✓ Saved quiz session to database", { difficultyMultiplier: difficultyMultiplier.toFixed(2) });

    // 3. Update knowledge state for the region
    // Use datasetSignature to group quizzes using the same GeoJSON file
    await updateKnowledgeState(state, category, geography, region, granularityLevel, config.datasetSignature, quizSignature);

    // 4. Sync to Supabase (fire-and-forget for logged-in users)
    // This enables detailed analytics and cross-device history
    syncQuizRunToSupabase(sessionId).catch((error) => {
      console.warn("⚠️ Failed to sync quiz run to Supabase (will retry later):", error);
      // Don't throw - local data is already saved, cloud sync is optional
    });

  } catch (error) {
    console.error("Failed to save quiz results:", error);
    throw error;
  }
}

/**
 * Update knowledge state based on quiz session
 * Knowledge state tracks regional completion progress
 */
async function updateKnowledgeState(
  state: QuizState,
  category: Category,
  geography: string,
  region: string,
  granularityLevel: number,
  datasetSignature: string,
  quizSignature: string
): Promise<void> {
  const now = new Date().toISOString();

  const knowledgeId = `${category}:${region}:${datasetSignature}`;

  // Get existing knowledge state
  const existing = await db.knowledge_state.get(knowledgeId);

  // Calculate completion metrics
  const totalPrompts = state.order.length;
  // Count only CORRECT answers as "completed" (mastered)
  const completedPrompts = Object.values(state.selections).filter((s) => s === "correct").length;
  const completionRate = totalPrompts > 0 ? (completedPrompts / totalPrompts) * 100 : 0;

  // Check for perfect completion
  const achievedPerfection = completionRate === 100;

  const perfectCompletionDate = achievedPerfection ? now : (existing?.perfectCompletionDate || null);

  const knowledgeRecord: KnowledgeState = {
    id: knowledgeId,
    category,
    geography,
    region,
    datasetSignature,
    granularityLevel,
    totalPrompts,
    completedPrompts,
    completionRate,
    achievedPerfection: achievedPerfection || (existing?.achievedPerfection || false),
    perfectCompletionDate,
    lastQuizSignature: quizSignature,
    lastUpdated: now,
    attemptsCount: (existing?.attemptsCount || 0) + 1,
  };

  await db.knowledge_state.put(knowledgeRecord);
  console.log(`✓ Updated knowledge state for ${geography} / ${region} (${Math.round(completionRate)}% completion)`);
}

/**
 * Get current quiz stats
 */
export function getQuizStats() {
  const state = quizState;
  const totalQuestions = state.order.length;
  const answeredQuestions = Object.keys(state.selections).length;
  const correctAnswers = Object.values(state.selections).filter(
    (s) => s === "correct"
  ).length;
  const incorrectAnswers = answeredQuestions - correctAnswers;
  const accuracyRate = answeredQuestions > 0
    ? (correctAnswers / answeredQuestions) * 100
    : 0;

  return {
    totalQuestions,
    answeredQuestions,
    correctAnswers,
    incorrectAnswers,
    accuracyRate,
  };
}

// ============================================================================
// Selectors (common queries)
// ============================================================================

export const selectMode = (state: QuizState) => state.mode;
export const selectConfig = (state: QuizState) => state.config;
export const selectFeatures = (state: QuizState) => state.features;
export const selectCurrentIndex = (state: QuizState) => state.currentIndex;
export const selectOrder = (state: QuizState) => state.order;
export const selectLastAnswer = (state: QuizState) => state.lastAnswer;
export const selectAttempts = (state: QuizState) => state.attempts;
export const selectMapSettings = (state: QuizState) => state.mapSettings;
export const selectSettingsLocked = (state: QuizState) => state.settingsLockedAt !== null;

/**
 * Get current prompt/question
 */
export function selectCurrentPrompt(state: QuizState) {
  if (state.mode !== "playing") return null;

  const promptId = state.order[state.currentIndex];
  if (!promptId) {
    console.warn("No promptId at index", state.currentIndex, "order length:", state.order.length);
    return null;
  }

  // Extract group ID from prompt ID (format: "groupId::repIndex")
  const groupId = promptId.split("::")[0];
  if (!groupId) {
    console.warn("Invalid promptId format:", promptId);
    return null;
  }

  // Get the prompt group
  const promptGroup = state.promptGroupMap[groupId];
  if (!promptGroup) {
    console.warn("Prompt group not found:", groupId);
    return null;
  }

  // Use first feature for display purposes
  const firstFeatureId = promptGroup.featureIds[0];
  const feature = state.features.find((f) => f.id === firstFeatureId);
  if (!feature) {
    console.warn("Feature not found:", firstFeatureId);
    return null;
  }

  console.log("Current prompt ready:", {
    promptId,
    groupId,
    validFeatureIds: promptGroup.featureIds,
    promptParts: promptGroup.parts
  });

  return {
    promptId,
    featureId: firstFeatureId, // For backwards compatibility
    validFeatureIds: promptGroup.featureIds, // All valid answers
    feature,
    promptParts: promptGroup.parts,
    promptText: promptGroup.parts.map((p) => p.value).join(", "),
    attemptsRemaining: (state.config?.maxAttemptsPerPrompt ?? 1) - state.currentPromptAttempts,
  };
}

/**
 * Get quiz progress percentage
 */
export function selectProgress(state: QuizState) {
  const total = state.order.length;
  if (total === 0) return 0;
  return (state.currentIndex / total) * 100;
}
